import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import { Plus, Search, Edit2, Trash2, X, Save, Mail, MapPin, Store, ChevronDown, ChevronUp } from 'lucide-react';

export default function Vendors() {
  const { user } = useAuth();
  const role = user?.role;
  const canEdit = ['admin', 'md', 'purchase'].includes(role);
  const canDelete = ['admin', 'md'].includes(role);
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [materialInput, setMaterialInput] = useState('');
  const [viewTab, setViewTab] = useState('vendors');
  const [hsnList, setHsnList] = useState([]);
  const [hsnSearch, setHsnSearch] = useState('');

  const emptyForm = { name: '', contact_person: '', phone: '', email: '', address: '', gstin: '', materials_supplied: [] };
  const [form, setForm] = useState({ ...emptyForm });

  const fetchVendors = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (!showInactive) params.set('is_active', 'true');
    api.get(`/vendors?${params}`).then(r => setVendors(r.data.vendors || [])).catch(() => {});
  };
  const fetchHSN = () => api.get('/materials').then(r => setHsnList(r.data.materials || [])).catch(() => {});

  useEffect(() => { fetchVendors(); }, [search, showInactive]);
  useEffect(() => { if (viewTab === 'hsn') fetchHSN(); }, [viewTab]);

  const addMaterial = () => { const val = materialInput.trim(); if (!val) return; if (form.materials_supplied.includes(val.toLowerCase())) { toast.error('Already added'); return; } setForm(f => ({ ...f, materials_supplied: [...f.materials_supplied, val.toLowerCase()] })); setMaterialInput(''); };
  const removeMaterial = (idx) => { setForm(f => ({ ...f, materials_supplied: f.materials_supplied.filter((_, i) => i !== idx) })); };
  const handleCreate = async (e) => { e.preventDefault(); if (!form.name.trim()) { toast.error('Vendor name required'); return; } try { await api.post('/vendors', form); toast.success('Vendor added'); setForm({ ...emptyForm }); setShowAdd(false); setMaterialInput(''); fetchVendors(); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); } };
  const startEdit = (v) => { setEditId(v.id); setExpandedId(v.id); setForm({ name: v.name || '', contact_person: v.contact_person || '', phone: v.phone || '', email: v.email || '', address: v.address || '', gstin: v.gstin || '', materials_supplied: v.materials_supplied || [] }); setMaterialInput(''); };
  const handleUpdate = async () => { if (!form.name.trim()) { toast.error('Vendor name required'); return; } try { await api.put(`/vendors/${editId}`, form); toast.success('Vendor updated'); setEditId(null); setForm({ ...emptyForm }); setMaterialInput(''); fetchVendors(); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); } };
  const handleDeactivate = async (id) => { if (!window.confirm('Deactivate this vendor?')) return; try { await api.delete(`/vendors/${id}`); toast.success('Vendor deactivated'); fetchVendors(); } catch { toast.error('Failed'); } };
  const handleReactivate = async (id) => { try { await api.put(`/vendors/${id}`, { is_active: true }); toast.success('Vendor reactivated'); fetchVendors(); } catch { toast.error('Failed'); } };

  const activeCount = vendors.filter(v => v.is_active).length;
  const filteredHSN = hsnList.filter(m => !hsnSearch || m.name?.toLowerCase().includes(hsnSearch.toLowerCase()) || m.hsn_code?.toLowerCase().includes(hsnSearch.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
          <p className="text-sm text-gray-500">{activeCount} active vendor{activeCount !== 1 ? 's' : ''}</p>
        </div>
        {canEdit && viewTab === 'vendors' && (
          <button onClick={() => { setShowAdd(!showAdd); setEditId(null); setForm({ ...emptyForm }); setMaterialInput(''); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-white rounded-xl text-xs font-medium">
            <Plus size={14} /> Add Vendor
          </button>
        )}
      </div>

      {/* Tab toggle */}
      <div className="flex gap-1 border-b pb-px">
        <button onClick={() => setViewTab('vendors')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${viewTab === 'vendors' ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500'}`}>Vendors</button>
        <button onClick={() => setViewTab('hsn')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${viewTab === 'hsn' ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500'}`}>HSN Directory</button>
      </div>

      {/* ═══ HSN DIRECTORY TAB ═══ */}
      {viewTab === 'hsn' && (
        <div className="space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={hsnSearch} onChange={e => setHsnSearch(e.target.value)} placeholder="Search materials or HSN codes..." className="w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm" />
          </div>
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Material</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">HSN Code</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Category</th>
                </tr>
              </thead>
              <tbody>
                {filteredHSN.map(m => (
                  <tr key={m.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium">{m.name}</td>
                    <td className="px-4 py-2.5"><span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-mono">{m.hsn_code || '—'}</span></td>
                    <td className="px-4 py-2.5 text-gray-500 capitalize">{m.category || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredHSN.length === 0 && <p className="text-center text-gray-400 text-sm py-6">{hsnSearch ? 'No materials match' : 'No materials in HSN directory'}</p>}
          </div>
          <p className="text-xs text-gray-400">Showing {filteredHSN.length} of {hsnList.length} materials. HSN codes are auto-applied when creating Purchase Orders.</p>
        </div>
      )}

      {/* ═══ VENDORS TAB ═══ */}
      {viewTab === 'vendors' && (<>
        {/* Search */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors by name, contact, phone..." className="w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm" />
          </div>
          <button onClick={() => setShowInactive(!showInactive)} className={`px-3 py-2 rounded-xl text-xs font-medium border transition ${showInactive ? 'bg-gray-100 text-gray-700' : 'bg-white text-gray-400'}`}>
            {showInactive ? 'Hide Inactive' : 'Show Inactive'}
          </button>
        </div>

        {/* Add / Edit form */}
        {(showAdd || editId) && (
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <p className="text-sm font-bold text-gray-700 uppercase">{editId ? 'Edit Vendor' : 'Add New Vendor'}</p>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Vendor / Company Name *" className="w-full px-3 py-2.5 border rounded-lg text-sm" />
            <div className="flex gap-3">
              <input value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} placeholder="Contact Person" className="flex-1 px-3 py-2.5 border rounded-lg text-sm" />
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" className="flex-1 px-3 py-2.5 border rounded-lg text-sm" />
            </div>
            <div className="flex gap-3">
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="flex-1 px-3 py-2.5 border rounded-lg text-sm" />
              <input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))} placeholder="GSTIN" className="flex-1 px-3 py-2.5 border rounded-lg text-sm" />
            </div>
            <textarea value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Address" rows={2} className="w-full px-3 py-2.5 border rounded-lg text-sm" />
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-1">Materials Supplied / HSN Codes</p>
              <div className="flex gap-2">
                <input value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addMaterial(); } }} placeholder="e.g. Plywood (HSN: 4412)" className="flex-1 px-3 py-2 border rounded-lg text-sm" />
                <button type="button" onClick={addMaterial} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">Add</button>
              </div>
              {form.materials_supplied.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.materials_supplied.map((m, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-50 text-brand-700 rounded-full text-xs">
                      {m} <button type="button" onClick={() => removeMaterial(i)} className="hover:text-red-500"><X size={10} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setShowAdd(false); setEditId(null); setForm({ ...emptyForm }); }} className="flex-1 py-2.5 border rounded-xl text-sm">Cancel</button>
              {editId ? (
                <button type="button" onClick={handleUpdate} className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium"><span className="inline-flex items-center gap-1"><Save size={14} /> Save Changes</span></button>
              ) : (
                <button type="button" onClick={handleCreate} className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium"><span className="inline-flex items-center gap-1"><Plus size={14} /> Add Vendor</span></button>
              )}
            </div>
          </div>
        )}

        {/* Vendor list */}
        <div className="space-y-3">
          {vendors.map(v => {
            const expanded = expandedId === v.id && editId !== v.id;
            const mats = v.materials_supplied || [];
            return (
              <div key={v.id} className={`bg-white rounded-xl border overflow-hidden ${!v.is_active ? 'opacity-60' : ''}`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : v.id)}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0"><Store size={20} className="text-brand-600" /></div>
                      <div className="min-w-0">
                        <p className="font-semibold">{v.name}</p>
                        <p className="text-xs text-gray-400">
                          {v.contact_person && `${v.contact_person} · `}{v.phone && `📞 ${v.phone}`}{v.gstin && ` · GSTIN: ${v.gstin}`}
                          {!v.is_active && <span className="ml-2 text-red-500 font-medium">INACTIVE</span>}
                        </p>
                        {mats.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {mats.slice(0, 5).map((m, i) => (<span key={i} className="px-2 py-0.5 bg-brand-50 text-brand-700 rounded-full text-[10px]">{m}</span>))}
                            {mats.length > 5 && <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px]">+{mats.length - 5} more</span>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                    </div>
                  </div>
                </div>
                {expanded && (
                  <div className="px-4 pb-4 border-t pt-3 space-y-2">
                    {v.email && <p className="text-sm text-gray-600 flex items-center gap-1.5"><Mail size={12} className="text-gray-400" /> {v.email}</p>}
                    {v.address && <p className="text-sm text-gray-600 flex items-center gap-1.5"><MapPin size={12} className="text-gray-400" /> {v.address}</p>}
                    {v.gstin && <p className="text-sm text-gray-600">🏛️ GSTIN: <span className="font-mono">{v.gstin}</span></p>}
                    {mats.length > 0 && (
                      <div><p className="text-xs font-bold text-gray-500 uppercase mb-1">All Materials / HSN Codes</p>
                        <div className="flex flex-wrap gap-1.5">{mats.map((m, i) => (<span key={i} className="px-2.5 py-1 bg-brand-50 text-brand-700 rounded-full text-xs">{m}</span>))}</div>
                      </div>
                    )}
                    {canEdit && (
                      <div className="flex gap-2 pt-2">
                        <button onClick={(e) => { e.stopPropagation(); startEdit(v); }} className="flex items-center gap-1 px-3 py-1.5 bg-brand-100 text-brand-700 rounded-lg text-xs font-medium"><Edit2 size={12} /> Edit</button>
                        {canDelete && v.is_active && (
                          <button onClick={(e) => { e.stopPropagation(); handleDeactivate(v.id); }} className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium"><Trash2 size={12} /> Deactivate</button>
                        )}
                        {canDelete && !v.is_active && (
                          <button onClick={(e) => { e.stopPropagation(); handleReactivate(v.id); }} className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium">Reactivate</button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {vendors.length === 0 && (
            <div className="text-center py-12">
              <Store size={48} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">{search ? 'No vendors match your search' : 'No vendors yet. Add your first vendor.'}</p>
            </div>
          )}
        </div>
      </>)}
    </div>
  );
}
